import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useForm, type FieldPath, type UseFormRegisterReturn } from "react-hook-form";
import { trackJourneyEvent } from "@/utils/analytics";
import {
  currentSourcePage,
  getRetryKey,
  PATHWAYS_SCHEMA_VERSION,
  PRIVACY_NOTICE_VERSION,
  safeSummaryValue,
  splitContactName,
  validateContactName,
  validateContactPhone,
  TEAM_BUSINESS_TYPE_OPTIONS,
  TEAM_CONTACT_ROLE_OPTIONS,
  TEAM_COURSE_OPTIONS,
  TEAM_DELIVERY_OPTIONS,
  TEAM_GOAL_OPTIONS,
  TEAM_LEARNER_BAND_OPTIONS,
  TEAM_STAFF_BACKGROUND_OPTIONS,
  TEAM_TIMEFRAME_OPTIONS,
} from "@/utils/enquiry-forms";
import type { PathwayPersona } from "@/utils/pathway-rules";

type BusinessTrainingRequestProps = {
  /** Kept for compatibility with existing embeds. */
  source: string;
  title?: string;
  intro?: string;
  compact?: boolean;
  sourcePage?: string;
  placement?: string;
  courseHint?: string;
  entryPersona?: PathwayPersona;
};

type FormValues = {
  contact_name: string;
  email: string;
  phone_number: string;
  preferred_contact_method: "email" | "phone" | "whatsapp";
  contact_role: string;
  business_name: string;
  learners_to_train_band: string;
  goal: string;
  course_keys: string[];
  staff_background_mix: string[];
  start_timeframe: string;
  delivery_interest: string;
  business_location: string;
  business_type: string;
  website: string;
  notes: string;
  marketing_email_opt_in: boolean;
};

const COURSE_KEYS = new Set<string>(TEAM_COURSE_OPTIONS.map(([key]) => key));

const inputClass = (hasError = false) =>
  `block w-full rounded-2xl border px-4 py-3 text-stone-900 shadow-sm transition focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200 ${hasError ? "border-red-500" : "border-stone-300"}`;

function FieldError({ message, id }: { message?: string; id?: string }) {
  return message ? <p id={id} className="mt-2 text-sm text-red-600">{message}</p> : null;
}

function SelectField({
  id,
  label,
  error,
  registration,
  options,
}: {
  id: string;
  label: string;
  error?: string;
  registration: UseFormRegisterReturn;
  options: ReadonlyArray<readonly [string, string]>;
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-2 block text-sm font-medium text-stone-800">{label}</label>
      <select id={id} aria-invalid={!!error} aria-describedby={error ? `${id}-error` : undefined} className={inputClass(!!error)} {...registration}>
        <option value="">Select</option>
        {options.map(([value, labelText]) => <option key={value} value={value}>{labelText}</option>)}
      </select>
      <FieldError id={`${id}-error`} message={error} />
    </div>
  );
}

export default function BusinessTrainingRequest({
  source,
  title = "Request a team training plan",
  intro = "Tell us what you want to offer, who needs training and their current experience. We will review suitable course options, entry requirements and scheduling with you.",
  compact = false,
  sourcePage,
  placement = "business_training_inline",
  courseHint,
  entryPersona,
}: BusinessTrainingRequestProps) {
  const [hydrated, setHydrated] = useState(false);
  const [state, setState] = useState<{
    complete: boolean;
    loading: boolean;
    error: string | null;
    reference: string | null;
    submitted: FormValues | null;
  }>({ complete: false, loading: false, error: null, reference: null, submitted: null });
  const retryRef = useRef<{ fingerprint: string; key: string } | null>(null);
  const startedRef = useRef(false);
  const validatedCourseHint = courseHint && COURSE_KEYS.has(courseHint) ? courseHint : undefined;
  const defaultCourseKeys = validatedCourseHint ? [validatedCourseHint] : [];
  const {
    register,
    handleSubmit,
    setValue,
    setError,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: {
      preferred_contact_method: "email",
      learners_to_train_band: "",
      goal: "",
      course_keys: defaultCourseKeys,
      staff_background_mix: [],
      start_timeframe: "unknown",
      delivery_interest: "unsure",
      business_type: "",
      marketing_email_opt_in: false,
    },
  });
  const preferredContact = watch("preferred_contact_method");

  useEffect(() => {
    setHydrated(true);
    if (validatedCourseHint) setValue("course_keys", [validatedCourseHint]);
  }, [validatedCourseHint, setValue]);

  const markStarted = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    trackJourneyEvent("team_enquiry_started", {
      form_id: "business-training-form",
      form_type: "business-training",
      audience: "team",
      page_key: (sourcePage || source).split("/").filter(Boolean).pop() || "business_training",
      placement,
      course_key: validatedCourseHint,
      persona: entryPersona,
    });
  };

  const onSubmit = async (data: FormValues) => {
    const { first_name, last_name } = splitContactName(data.contact_name);
    const teamTraining = {
      business_name: data.business_name.trim(),
      learners_to_train_band: data.learners_to_train_band,
      goal: data.goal,
      course_keys: data.course_keys || [],
      staff_background_mix: data.staff_background_mix || [],
      start_timeframe: data.start_timeframe,
      delivery_interest: data.delivery_interest,
      business_location: data.business_location.trim(),
      business_type: data.business_type,
      ...(data.contact_role ? { contact_role: data.contact_role } : {}),
      website: data.website.trim(),
      notes: data.notes.trim(),
    };
    const summary = [
      "Team-training enquiry",
      `Business/contact: ${data.business_name.trim()} / ${data.contact_name.trim()}`,
      `Staff to train: ${data.learners_to_train_band}`,
      `Goal: ${data.goal}`,
      `Course or area: ${(data.course_keys || []).join(", ") || "Not provided"}`,
      `Staff background mix: ${(data.staff_background_mix || []).join(", ") || "Not provided"}`,
      `Timing: ${data.start_timeframe}`,
      `Attendance: ${data.delivery_interest}`,
      `Preferred contact: ${data.preferred_contact_method}`,
      `Placement: ${placement}`,
      data.business_location.trim() ? `Business location: ${data.business_location.trim()}` : "",
      data.notes.trim() ? `Additional context: ${data.notes.trim()}` : "",
    ].filter(Boolean).join("\n");
    const body = {
      type: "business-training",
      first_name,
      last_name,
      email: data.email.trim().toLowerCase(),
      phone_number: preferredContact === "email" ? "" : data.phone_number.trim(),
      message: summary,
      preferences: {
        schema_version: PATHWAYS_SCHEMA_VERSION,
        source_page: sourcePage || currentSourcePage(source),
        placement,
        form_id: "business-training-form",
        ...(entryPersona ? { entry_persona: entryPersona } : {}),
        preferred_contact_method: data.preferred_contact_method,
        booking_for: "team",
        lead_type: "team_training_enquiry",
        team_training: teamTraining,
      },
      permissions: {
        privacy_notice_version: PRIVACY_NOTICE_VERSION,
        marketing_email_opt_in: data.marketing_email_opt_in === true,
      },
    };
    const retry = getRetryKey(retryRef.current, body);
    retryRef.current = retry;

    try {
      setState((current) => ({ ...current, loading: true, error: null }));
      const response = await fetch("/api/callback/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...body, idempotency_key: retry.key }),
      });
      const result = await response.json();
      if (response.status === 400 && result?.fields && typeof result.fields === "object") {
        const mapping: Record<string, FieldPath<FormValues>> = {
          first_name: "contact_name",
          last_name: "contact_name",
          email: "email",
          phone_number: "phone_number",
          "preferences.preferred_contact_method": "preferred_contact_method",
          "preferences.team_training.business_name": "business_name",
          "preferences.team_training.learners_to_train_band": "learners_to_train_band",
          "preferences.team_training.goal": "goal",
          "preferences.team_training.course_keys": "course_keys",
          "preferences.team_training.staff_background_mix": "staff_background_mix",
          "preferences.team_training.start_timeframe": "start_timeframe",
          "preferences.team_training.delivery_interest": "delivery_interest",
          "preferences.team_training.business_location": "business_location",
          "preferences.team_training.location": "business_location",
          "preferences.team_training.business_type": "business_type",
          "preferences.team_training.contact_role": "contact_role",
          "preferences.team_training.website": "website",
          "preferences.team_training.notes": "notes",
        };
        for (const field of Object.keys(result.fields)) {
          const formField = mapping[field];
          if (formField) setError(formField, { type: "server", message: "Please check this answer" });
        }
        setState((current) => ({ ...current, loading: false, error: "Please review the highlighted fields and submit again." }));
        trackJourneyEvent("enquiry_submit_failed", { form_id: "business-training-form", form_type: "business-training", audience: "team", page_key: (sourcePage || source).split("/").filter(Boolean).pop() || "business_training", placement, failure_code: "validation" });
        return;
      }
      if (!response.ok || !result?.success) throw new Error("Unable to submit team enquiry");
      setState({
        complete: true,
        loading: false,
        error: null,
        reference: typeof result.reference === "string" ? result.reference : null,
        submitted: data,
      });
      if (result.created === true) {
        trackJourneyEvent("generate_lead", {
          form_id: "business-training-form",
          form_type: "business-training",
          audience: "team",
          page_key: (sourcePage || source).split("/").filter(Boolean).pop() || "business_training",
          placement,
          course_key: validatedCourseHint,
          persona: entryPersona,
        });
      }
    } catch {
      setState((current) => ({
        ...current,
        loading: false,
        error: "We could not confirm your request was received. Your answers are still here; please retry or call admissions.",
      }));
      trackJourneyEvent("enquiry_submit_failed", {
        form_id: "business-training-form",
        form_type: "business-training",
        audience: "team",
        page_key: (sourcePage || source).split("/").filter(Boolean).pop() || "business_training",
        placement,
        failure_code: "submission_unconfirmed",
      });
    }
  };

  if (state.complete && state.submitted) {
    return (
      <section id="planning-form" className="scroll-mt-24 rounded-[2rem] border border-emerald-200 bg-emerald-50 p-6 sm:p-8" aria-live="polite" data-private data-clarity-mask data-heap-redact-text data-sentry-block>
        <p className="inline-flex rounded-full border border-emerald-200 bg-white px-3 py-1 text-sm font-semibold text-emerald-700">Team enquiry received</p>
        <h2 className="mt-4 text-3xl font-black tracking-tight text-stone-900 sm:text-4xl">Your team-training enquiry has been received.</h2>
        <p className="mt-4 max-w-2xl text-base leading-7 text-stone-700">Our team will review the training requirement and contact you to discuss suitable options. Entry requirements, dates, delivery arrangements and pricing will be confirmed before booking.</p>
        {state.reference && <p className="mt-3 text-sm text-stone-600">Reference: <span className="font-semibold">{state.reference}</span></p>}
        <dl className="mt-6 grid gap-3 text-sm text-stone-700 sm:grid-cols-2">
          <div><dt className="font-semibold">Business</dt><dd>{safeSummaryValue(state.submitted.business_name)}</dd></div>
          <div><dt className="font-semibold">Staff to train</dt><dd>{safeSummaryValue(TEAM_LEARNER_BAND_OPTIONS.find(([key]) => key === state.submitted?.learners_to_train_band)?.[1])}</dd></div>
          <div><dt className="font-semibold">Training goal</dt><dd>{safeSummaryValue(TEAM_GOAL_OPTIONS.find(([key]) => key === state.submitted?.goal)?.[1])}</dd></div>
          <div><dt className="font-semibold">Preferred contact</dt><dd>{safeSummaryValue(state.submitted.preferred_contact_method)}</dd></div>
        </dl>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Link href="/training-course-calendar/" className="inline-flex items-center justify-center rounded-full bg-stone-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-brand-600">Explore current course dates</Link>
          <Link href="/which-aesthetics-course-is-right-for-me/" className="inline-flex items-center justify-center rounded-full border border-stone-900 px-5 py-3 text-sm font-semibold text-stone-900 transition hover:border-brand-600 hover:text-brand-600">Explore individual training</Link>
        </div>
      </section>
    );
  }

  return (
    <section id="planning-form" className="scroll-mt-24 rounded-[2rem] border border-stone-200 bg-white p-6 shadow-[0_24px_80px_rgba(28,25,23,0.08)] sm:p-8 lg:p-10">
      <div className="max-w-2xl">
        <p className="text-sm font-semibold uppercase tracking-[0.22em] text-brand-600">Team training request</p>
        <h2 className="mt-3 text-3xl font-black tracking-tight text-stone-900 sm:text-4xl">{title}</h2>
        <p className="mt-4 text-base leading-7 text-stone-600">{intro}</p>
      </div>

      <noscript>
        <div className="mt-6 rounded-2xl border border-stone-200 bg-stone-50 p-5 text-sm leading-6 text-stone-700">
          JavaScript is unavailable, so this form cannot be submitted. Email <a className="font-semibold underline" href="mailto:admissions@cosmetic.college">admissions@cosmetic.college</a> or call <a className="font-semibold underline" href="tel:+443330155117">0333 015 5117</a> to discuss team training.
        </div>
      </noscript>
      <form onSubmit={handleSubmit(onSubmit)} onFocus={markStarted} className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-2" data-private data-clarity-mask data-heap-redact-text data-sentry-block>
        <fieldset className="grid grid-cols-1 gap-5 md:col-span-2 md:grid-cols-2">
          <legend className="mb-2 text-lg font-bold text-stone-900">Training requirement</legend>
          <SelectField id="business-training-band" label="How many staff need training?" error={errors.learners_to_train_band?.message} registration={register("learners_to_train_band", { required: "Choose the number of staff to train" })} options={TEAM_LEARNER_BAND_OPTIONS} />
          <SelectField id="business-training-goal" label="What is the main training goal?" error={errors.goal?.message} registration={register("goal", { required: "Choose the main goal" })} options={TEAM_GOAL_OPTIONS} />

          <div className="md:col-span-2">
            <fieldset>
              <legend className="mb-3 block text-sm font-medium text-stone-800">Relevant course or area <span className="font-normal text-stone-500">(optional, select all that apply)</span></legend>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {TEAM_COURSE_OPTIONS.map(([value, label]) => <label key={value} className="flex items-start gap-3 rounded-xl border border-stone-200 px-3 py-3 text-sm text-stone-700"><input type="checkbox" value={value} className="mt-0.5 h-4 w-4 rounded border-stone-300 text-brand-600" {...register("course_keys")} /><span>{label}</span></label>)}
              </div>
              <p className="mt-2 text-xs text-stone-500">If your area is not listed, you can describe it in the optional notes.</p>
            </fieldset>
          </div>

          <div className="md:col-span-2">
            <fieldset>
              <legend className="mb-3 block text-sm font-medium text-stone-800">What experience do staff currently have? <span className="font-normal text-stone-500">(optional, select all that apply)</span></legend>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {TEAM_STAFF_BACKGROUND_OPTIONS.map(([value, label]) => <label key={value} className="flex items-start gap-3 rounded-xl border border-stone-200 px-3 py-3 text-sm text-stone-700"><input type="checkbox" value={value} className="mt-0.5 h-4 w-4 rounded border-stone-300 text-brand-600" {...register("staff_background_mix")} /><span>{label}</span></label>)}
              </div>
              <p className="mt-2 text-xs text-stone-500">This describes the team overall and does not confirm an individual&apos;s entry requirements.</p>
            </fieldset>
          </div>
          <SelectField id="business-training-timeframe" label="When are you hoping to arrange training?" registration={register("start_timeframe")} options={TEAM_TIMEFRAME_OPTIONS} />
          <SelectField id="business-training-delivery" label="Attendance preference" registration={register("delivery_interest")} options={TEAM_DELIVERY_OPTIONS} />
          {watch("delivery_interest") === "coordinated_private" && <p className="rounded-xl bg-amber-50 p-3 text-sm text-stone-700 md:col-span-2">Coordinated or private delivery is subject to course suitability and capacity review. It is not reserved by this enquiry.</p>}
          <div><label htmlFor="business-training-location" className="mb-2 block text-sm font-medium text-stone-800">Business location <span className="font-normal text-stone-500">(optional)</span></label><input id="business-training-location" maxLength={100} placeholder="Town or outward postcode" aria-invalid={!!errors.business_location} aria-describedby={errors.business_location ? "business-training-location-error" : undefined} className={inputClass(!!errors.business_location)} {...register("business_location", { maxLength: { value: 100, message: "Use 100 characters or fewer" } })} /><FieldError id="business-training-location-error" message={errors.business_location?.message} /></div>
          <SelectField id="business-training-type" label="Business type (optional)" registration={register("business_type")} options={TEAM_BUSINESS_TYPE_OPTIONS} />
        </fieldset>

        <fieldset className="grid grid-cols-1 gap-5 md:col-span-2 md:grid-cols-2">
          <legend className="mb-2 text-lg font-bold text-stone-900">Business contact</legend>
          <div><label htmlFor="business-training-contact-name" className="mb-2 block text-sm font-medium text-stone-800">Contact name</label><input id="business-training-contact-name" autoComplete="name" maxLength={201} aria-invalid={!!errors.contact_name} aria-describedby={errors.contact_name ? "business-training-contact-name-error" : undefined} className={inputClass(!!errors.contact_name)} {...register("contact_name", { required: "Contact name is required", maxLength: { value: 201, message: "Please use 200 characters or fewer" }, validate: validateContactName })} /><FieldError id="business-training-contact-name-error" message={errors.contact_name?.message} /></div>
          <SelectField id="business-training-role" label="Contact role (optional)" registration={register("contact_role")} options={TEAM_CONTACT_ROLE_OPTIONS} />
          <div><label htmlFor="business-training-business-name" className="mb-2 block text-sm font-medium text-stone-800">Business or trading name</label><input id="business-training-business-name" autoComplete="organization" maxLength={160} aria-invalid={!!errors.business_name} aria-describedby={errors.business_name ? "business-training-business-name-error" : undefined} className={inputClass(!!errors.business_name)} {...register("business_name", { required: "Business or trading name is required", maxLength: { value: 160, message: "Use 160 characters or fewer" } })} /><FieldError id="business-training-business-name-error" message={errors.business_name?.message} /></div>
          <div><label htmlFor="business-training-email" className="mb-2 block text-sm font-medium text-stone-800">Email</label><input id="business-training-email" type="email" autoComplete="email" maxLength={254} aria-invalid={!!errors.email} aria-describedby={errors.email ? "business-training-email-error" : undefined} className={inputClass(!!errors.email)} {...register("email", { required: "Email is required", maxLength: { value: 254, message: "Enter a valid email address" }, pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Enter a valid email address" } })} /><FieldError id="business-training-email-error" message={errors.email?.message} /></div>
          <SelectField id="business-training-contact-method" label="Preferred contact method" error={errors.preferred_contact_method?.message} registration={register("preferred_contact_method", { required: "Choose a preferred contact method" })} options={[["email", "Email"], ["phone", "Phone"], ["whatsapp", "WhatsApp"]]} />
          <div><label htmlFor="business-training-phone" className="mb-2 block text-sm font-medium text-stone-800">Phone number {preferredContact !== "email" ? <span aria-hidden="true">*</span> : <span className="font-normal text-stone-500">(only needed for phone or WhatsApp contact)</span>}</label><input id="business-training-phone" type="tel" autoComplete="tel" aria-required={preferredContact !== "email"} aria-invalid={!!errors.phone_number} aria-describedby={errors.phone_number ? "business-training-phone-error" : undefined} maxLength={40} className={inputClass(!!errors.phone_number)} {...register("phone_number", { validate: (value) => validateContactPhone(value || "", preferredContact !== "email"), maxLength: { value: 40, message: "Enter a valid phone number" } })} /><FieldError id="business-training-phone-error" message={errors.phone_number?.message} /></div>
          <div><label htmlFor="business-training-website" className="mb-2 block text-sm font-medium text-stone-800">Business website <span className="font-normal text-stone-500">(optional)</span></label><input id="business-training-website" type="url" inputMode="url" autoComplete="url" maxLength={200} placeholder="https://example.com" aria-invalid={!!errors.website} aria-describedby={errors.website ? "business-training-website-error" : undefined} className={inputClass(!!errors.website)} {...register("website", { maxLength: { value: 200, message: "Use 200 characters or fewer" }, validate: (value) => !value || /^https?:\/\/.+\..+/.test(value) || "Enter a valid HTTP or HTTPS website address" })} /><FieldError id="business-training-website-error" message={errors.website?.message} /></div>
          <div className="md:col-span-2"><label htmlFor="business-training-notes" className="mb-2 block text-sm font-medium text-stone-800">Anything else that would help us understand the request? <span className="font-normal text-stone-500">(optional)</span></label><p className="mb-2 text-xs text-stone-500">Up to 1,000 characters. Do not include staff medical information, client details or confidential treatment cases.</p><textarea id="business-training-notes" rows={compact ? 3 : 4} maxLength={1000} aria-invalid={!!errors.notes} aria-describedby={errors.notes ? "business-training-notes-error" : undefined} className={`${inputClass(!!errors.notes)} resize-y`} {...register("notes", { maxLength: { value: 1000, message: "Use 1,000 characters or fewer" } })} /><FieldError id="business-training-notes-error" message={errors.notes?.message} /></div>
        </fieldset>

        <div className="md:col-span-2">
          <label className="flex items-start gap-3 text-sm leading-6 text-stone-600"><input type="checkbox" className="mt-1 h-4 w-4 rounded border-stone-300 text-brand-600 focus:ring-brand-500" {...register("marketing_email_opt_in")} /><span>Email me Cosmetic College course news and offers. I can unsubscribe at any time.</span></label>
          <p className="mt-2 text-xs leading-5 text-stone-500">We use your details to respond to this enquiry. Your enquiry does not sign you or your staff up for marketing. See our <Link href="/privacy-policy/" className="underline">privacy policy</Link>.</p>
        </div>
        {state.error && <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 md:col-span-2" role="alert">{state.error}</p>}
        <div className="flex flex-col gap-4 border-t border-stone-200 pt-6 md:col-span-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-xl text-sm leading-6 text-stone-500">We review staff requirements individually. Dates, delivery arrangements, entry checks and pricing are confirmed before booking.</p>
          <button type="submit" disabled={!hydrated || state.loading} className="inline-flex items-center justify-center rounded-full bg-stone-900 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60">{state.loading ? "Submitting…" : "Request a team training plan"}</button>
        </div>
      </form>
    </section>
  );
}
